import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    type OnChanges,
    type OnDestroy,
    type OnInit,
    Output,
    type SimpleChanges,
    inject,
} from '@angular/core';
import { NonNullableFormBuilder, type FormControl, ReactiveFormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import type { UntypedFormGroup } from '@angular/forms';
import type { AnimationSettings } from './type/animation-settings.type';
import { EAnimationType, EAnimationPosition, EAnimationSpeed } from './type/animation-settings.type';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { SpSwitchComponent } from '../../ui/sp-switch/src/sp-switch.component';
import { NgIf } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { TooltipModule } from 'ngx-bootstrap/tooltip';

@Component({
    selector: 'sp-animation-settings',
    templateUrl: './sp-animation-settings.component.html',
    styleUrls: ['./sp-animation-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [SpSwitchComponent, NgIf, ReactiveFormsModule, TranslateModule, TooltipModule],
})
export class SpAnimationSettingsComponent implements OnInit, OnDestroy, OnChanges {
    private readonly fb = inject(NonNullableFormBuilder);

    @Input() public animation?: AnimationSettings;
    @Input() public isText?: boolean;
    @Input() public isDisabled: boolean = false;
    @Output() public valueChangeEvent: EventEmitter<AnimationSettings> = new EventEmitter();

    private form: UntypedFormGroup;
    private readonly unsubscribe$ = new Subject<void>();

    public EAnimationType = EAnimationType;
    public EAnimationPosition = EAnimationPosition;
    public EAnimationSpeed = EAnimationSpeed;

    constructor() {
        this.initForm();
    }

    public ngOnInit(): void {
        this.form.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((changes) => this.emitChanges(changes));

        this.setFormData();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        const { animation } = changes;

        if (!animation?.isFirstChange() && animation?.currentValue !== animation?.previousValue) {
            this.setFormData();
        }
    }

    private initForm(): void {
        this.form = this.fb.group({
            isActive: null,
            animationType: null,
            animationPosition: null,
            animationSpeed: null,
        });
    }

    private setFormData(): void {
        const base: AnimationSettings = {
            isActive: this.isDisabled ? false : this.animation?.isActive,
            animationType: this.animation?.animationType,
            animationPosition: this.animation?.animationPosition,
            animationSpeed: this.animation?.animationSpeed,
        };

        this.form.patchValue(base, { emitEvent: false });
    }

    private emitChanges(changes: AnimationSettings): void {
        this.valueChangeEvent.emit(changes);
    }

    public get isActive(): FormControl<boolean> {
        return this.form.get('isActive') as FormControl<boolean>;
    }

    public get animationType(): FormControl<EAnimationType> {
        return this.form.get('animationType') as FormControl<EAnimationType>;
    }

    public get animationPosition(): FormControl<EAnimationPosition> {
        return this.form.get('animationPosition') as FormControl<EAnimationPosition>;
    }

    public get animationSpeed(): FormControl<EAnimationSpeed> {
        return this.form.get('animationSpeed') as FormControl<EAnimationSpeed>;
    }

    public toggleActiveAnimation(value: boolean): void {
        this.isActive.setValue(value);
    }

    public setAnimation(animation) {
        if (animation === EAnimationType.runningStripe) {
            this.animationPosition.patchValue(EAnimationPosition.right);
        }
    }

    public ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }
}
